import { Breadcrumb, Button, Divider, Tooltip, theme } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import './PageHeader.less';

/**
 * 页面顶栏：左侧为标题区，右侧可选面包屑（与 wisdomfusion-admin-ui feature-hmkx PageHeader 行为一致）。
 *
 * @param {object} props
 * @param {import('react').ReactNode} [props.children] - 左侧内容；不传时可使用 title
 * @param {import('react').ReactNode} [props.title] - 简单标题（仅当 children 为空时生效）
 * @param {Array<string | { label: string, path?: string }>} [props.breadcrumb]
 * @param {string} [props.className] - 根节点附加类名（如 scoring-setting）
 * @param {boolean} [props.showFilterToggle] - 是否渲染筛选显隐开关
 * @param {boolean} [props.filterVisible=true] - 受控：筛选面板是否可见
 * @param {(visible: boolean) => void} [props.onFilterVisibleChange] - 筛选面板显隐变更回调
 */
export default function PageHeader({ children, title, breadcrumb = [], className = '', showFilterToggle = false, filterVisible = true, onFilterVisibleChange }) {
    const { token } = theme.useToken();
    const { pathname } = useLocation();

    const rootClass = ['page-header', className].filter(Boolean).join(' ');

    const barStyle = {
        marginBottom: token.marginMD,
        background: token.colorBgContainer,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: token.marginSM,
    };

    const leftStyle = {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        flexWrap: 'wrap',
        gap: token.marginXS,
    };

    const breadcrumbTextStyle = {
        color: token.colorTextDescription,
        fontSize: token.fontSize,
    };

    const activeBreadcrumbTextStyle = {
        color: token.colorTextHeading,
        fontSize: token.fontSize,
    };

    const items = breadcrumb.map((entry, index) => {
        const isLast = index === breadcrumb.length - 1;
        const label = typeof entry === 'string' ? entry : entry.label;
        const path = typeof entry === 'string' ? undefined : entry.path;
        const style = isLast ? activeBreadcrumbTextStyle : breadcrumbTextStyle;
        const isCurrent = !!path && path === pathname;
        const itemKey = path ?? `${index}-${label}`;
        const linkOrSpan =
            !isLast && path && !isCurrent ? (
                <Link key={itemKey} to={path} style={style}>
                    {label}
                </Link>
            ) : (
                <span key={itemKey} style={style}>
                    {label}
                </span>
            );
        return { title: linkOrSpan };
    });

    const leftContent = children ?? title ?? null;
    const hasBreadcrumb = breadcrumb.length > 0;
    const showRight = hasBreadcrumb || showFilterToggle;
    const filterToggleLabel = filterVisible ? '隐藏筛选条件' : '显示筛选条件';

    const handleFilterToggle = () => {
        onFilterVisibleChange?.(!filterVisible);
    };

    return (
        <div className={rootClass} style={barStyle}>
            <div className="page-header__left" style={leftStyle}>
                {leftContent}
            </div>
            {showRight && (
                <div
                    className="page-header__right"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: token.marginXXS,
                    }}
                >
                    {hasBreadcrumb && (
                        <div
                            className="page-header__breadcrumb"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: token.marginXXS,
                            }}
                        >
                            <Tooltip title="当前位置">
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        cursor: 'default',
                                        marginRight: token.marginXXS,
                                    }}
                                    aria-label="当前位置"
                                >
                                    <i
                                        className="bi bi-diagram-3"
                                        style={{
                                            color: token.colorTextDescription,
                                            fontSize: token.fontSize,
                                        }}
                                        aria-hidden
                                    />
                                </span>
                            </Tooltip>
                            <Breadcrumb items={items} />
                        </div>
                    )}
                    {hasBreadcrumb && showFilterToggle && (
                        <Divider
                            type="vertical"
                            style={{
                                height: token.controlHeight,
                                marginBlock: 0,
                                marginInline: token.marginXS,
                            }}
                        />
                    )}
                    {showFilterToggle && (
                        <Tooltip title={filterToggleLabel}>
                            <Button
                                type="default"
                                className={filterVisible ? 'page-header__filter-toggle' : 'page-header__filter-toggle page-header__filter-toggle--hidden'}
                                icon={<span className="bi bi-funnel" aria-hidden="true" />}
                                aria-label={filterToggleLabel}
                                aria-pressed={filterVisible}
                                onClick={handleFilterToggle}
                                style={
                                    filterVisible
                                        ? undefined
                                        : {
                                              background: token.colorWarningBg,
                                              borderColor: token.colorWarningBorder,
                                              color: token.colorWarning,
                                          }
                                }
                            />
                        </Tooltip>
                    )}
                </div>
            )}
        </div>
    );
}
